/**
 * `src/lib/ui/theme/settings.ts` — the reading preferences, read from and
 * written to the FROZEN `settings` table through the existing Dexie `db`.
 *
 * It serves both of them — `ui.theme` and `ui.type_scale` — because they are
 * applied by the same boot call and are the only two device-scoped reading
 * preferences there are. Splitting them would mean two copies of the same
 * twelve lines of Dexie, and the second copy is the one that forgets
 * `device_scoped`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE FOUR RULES THIS FILE EXISTS TO GET RIGHT
 * ═══════════════════════════════════════════════════════════════════════════
 *  1. NO NEW KEYS. `src/lib/db/schema.ts` is byte-frozen and CI compares it
 *     against the `frozen-contracts-v1` tag. `SettingKey` is the closed set;
 *     `writeSetting` will not accept anything outside it, and TypeScript stops
 *     it at the call site as well.
 *  2. `device_scoped` COMES FROM THE SCHEMA, NEVER FROM HERE. `isDeviceScoped()`
 *     is the single source of truth and both keys are in its set — a type scale
 *     chosen on a 6" phone must not follow the user onto a 27" monitor, and
 *     `device_scoped = 1` is what excludes the row from every export. Hard-
 *     coding `1` here would be a second policy constant, and the schema's own
 *     docblock records what happened the last time this project had two.
 *  3. A FAILED READ IS A DEFAULT, NOT A CRASH. IndexedDB is unavailable in a
 *     private window on some engines, during a storage-eviction race, and
 *     whenever the user's profile is corrupt. SPEC §1.1: "IndexedDB is a cache."
 *     The reading preferences are the least important thing in the database, so
 *     a read that throws renders `SETTING_DEFAULTS` and the app still opens.
 *  4. THE CLOCK IS READ ONCE PER WRITE. `updated_at` feeds `mergeSetting()`'s
 *     lexicographic `(updated_at, canonicalize(value), device_scoped)` maximum.
 *     Two writes in one tick are resolved by the canonical value, which is
 *     deterministic — but only if both rows carry the timestamp they were
 *     actually written at.
 */

import {
	SETTING_DEFAULTS,
	db,
	isDeviceScoped,
	type SettingKey,
	type SettingsMap
} from '$lib/db/schema';

import { decodeTypeScale, encodeTypeScale, type TypeScale } from '$lib/ui/type/scale';
import { asThemeSetting, type ThemeSetting } from './theme';

export interface ReadingPreferences {
	readonly theme: ThemeSetting;
	readonly scale: TypeScale;
}

export const DEFAULT_PREFERENCES: ReadingPreferences = {
	theme: asThemeSetting(SETTING_DEFAULTS['ui.theme']),
	scale: decodeTypeScale(SETTING_DEFAULTS['ui.type_scale'])
};

/** Read one setting, falling back to the schema's own default. */
export async function readSetting<K extends SettingKey>(key: K): Promise<unknown> {
	try {
		const row = await db.settings.get(key);
		return row === undefined ? SETTING_DEFAULTS[key] : row.value;
	} catch {
		return SETTING_DEFAULTS[key];
	}
}

/**
 * Write one setting. `device_scoped` is taken from the schema, never asserted
 * here — see rule 2.
 *
 * Returns `false` when the write failed, so a settings screen can say so
 * instead of showing a choice that will not survive a reload. It does not
 * throw: a preference that cannot be persisted is still a preference that can
 * be applied for this session.
 */
export async function writeSetting<K extends SettingKey>(
	key: K,
	value: SettingsMap[K]
): Promise<boolean> {
	try {
		await db.settings.put({
			key,
			value: plain(value),
			updated_at: Date.now(),
			device_scoped: isDeviceScoped(key)
		});
		return true;
	} catch {
		return false;
	}
}

/**
 * A structured-clone-safe copy of a setting value.
 *
 * Callers are Svelte components, and an object held in `$state` is a Proxy —
 * which IndexedDB's structured clone refuses with `DataCloneError`, so saving
 * the prayer-time location failed on the very first run. Setting values are
 * plain JSON by contract (they are `unknown` at rest), so a JSON round trip is
 * exactly the right copy and strips the proxy.
 */
function plain<T>(value: T): T {
	return value !== null && typeof value === 'object'
		? (JSON.parse(JSON.stringify(value)) as T)
		: value;
}

/** Both reading preferences, in one pass, with defaults on any failure. */
export async function loadReadingPreferences(): Promise<ReadingPreferences> {
	const [theme, scale] = await Promise.all([readSetting('ui.theme'), readSetting('ui.type_scale')]);
	return { theme: asThemeSetting(theme), scale: decodeTypeScale(scale) };
}

export async function saveTheme(theme: ThemeSetting): Promise<boolean> {
	return writeSetting('ui.theme', theme);
}

/** Persist the packed reading size + leading. The packing is in
 *  `src/lib/ui/type/scale.ts`; this only ever writes a `number`, which is what
 *  `SettingsMap['ui.type_scale']` declares. */
export async function saveTypeScale(scale: TypeScale): Promise<boolean> {
	return writeSetting('ui.type_scale', encodeTypeScale(scale));
}
